import { lazy, ComponentType } from "react";
import { Route, Navigate } from "react-router-dom";

// Helper to retry lazy imports on chunk load failure
const CHUNK_RELOAD_KEY = 'chunk_reload_count';
const MAX_CHUNK_RETRIES = 2;

const lazyWithRetry = <T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) =>
  lazy(async () => {
    try {
      const result = await componentImport();
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return result;
    } catch (error) {
      const reloadCount = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0', 10);
      if (reloadCount < MAX_CHUNK_RETRIES) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(reloadCount + 1));
        window.location.reload();
        return { default: (() => null) as unknown as T };
      }
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      throw error;
    }
  });

// Admin page imports
const Dashboard = lazyWithRetry(() => import("@/pages/admin/Dashboard"));
const Reservations = lazyWithRetry(() => import("@/pages/admin/Reservations"));
const FollowUpCalendar = lazyWithRetry(() => import("@/pages/admin/FollowUpCalendar"));
const SalesPerformance = lazyWithRetry(() => import("@/pages/admin/SalesPerformance"));
const Leads = lazyWithRetry(() => import("@/pages/admin/Leads"));
const Customers = lazyWithRetry(() => import("@/pages/admin/Customers"));
const Jobs = lazyWithRetry(() => import("@/pages/admin/Jobs"));
const LocalJobs = lazyWithRetry(() => import("@/pages/admin/jobs/LocalJobs"));
const BillOfLading = lazyWithRetry(() => import("@/pages/admin/jobs/BillOfLading"));
const Estimates = lazyWithRetry(() => import("@/pages/admin/Estimates"));
const Billing = lazyWithRetry(() => import("@/pages/admin/Billing"));
const Accounting = lazyWithRetry(() => import("@/pages/admin/Accounting"));
const CCPayment = lazyWithRetry(() => import("@/pages/admin/billing/CCPayment"));
const OtherPaymentOptions = lazyWithRetry(() => import("@/pages/admin/billing/OtherPaymentOptions"));
const Fleet = lazyWithRetry(() => import("@/pages/admin/Fleet"));
const Staff = lazyWithRetry(() => import("@/pages/admin/Staff"));
const Calendar = lazyWithRetry(() => import("@/pages/admin/calendar/PickUp"));
const TransDeliveryCalendar = lazyWithRetry(() => import("@/pages/admin/calendar/TransDelivery"));
const FleetDispatchBoard = lazyWithRetry(() => import("@/pages/admin/calendar/FleetDispatchBoard"));
const Tasks = lazyWithRetry(() => import("@/pages/admin/Tasks"));
const InProgress = lazyWithRetry(() => import("@/pages/admin/InProgress"));
const Teams = lazyWithRetry(() => import("@/pages/admin/Teams"));
const TeamPerformance = lazyWithRetry(() => import("@/pages/admin/TeamPerformance"));
const Reports = lazyWithRetry(() => import("@/pages/admin/Reports"));
const TemplateAnalytics = lazyWithRetry(() => import("@/pages/admin/reports/TemplateAnalytics"));
const InventoryReconciliation = lazyWithRetry(() => import("@/pages/admin/reports/InventoryReconciliation"));
const BrokerDeals = lazyWithRetry(() => import("@/pages/admin/reports/BrokerDeals"));
const Settings = lazyWithRetry(() => import("@/pages/admin/Settings"));
const NotificationPreferences = lazyWithRetry(() => import("@/pages/admin/maintenance/NotificationPreferences"));
const Notifications = lazyWithRetry(() => import("@/pages/admin/Notifications"));
const Reviews = lazyWithRetry(() => import("@/pages/admin/Reviews"));
const VibeChat = lazyWithRetry(() => import("@/pages/admin/VibeChat"));
const ChatManagement = lazyWithRetry(() => import("@/pages/admin/ChatManagement"));
const VibeCapabilities = lazyWithRetry(() => import("@/pages/admin/VibeCapabilities"));
const FeaturesHub = lazyWithRetry(() => import("@/pages/admin/FeaturesHub"));
const InventoryCatalog = lazyWithRetry(() => import("@/pages/admin/maintenance/InventoryCatalog"));
const InterstateRates = lazyWithRetry(() => import("@/pages/admin/maintenance/InterstateRates"));
const LocalRateManager = lazyWithRetry(() => import("@/pages/admin/maintenance/LocalRateManager"));
const VolumeDiscounts = lazyWithRetry(() => import("@/pages/admin/maintenance/VolumeDiscounts"));
const Surcharges = lazyWithRetry(() => import("@/pages/admin/maintenance/Surcharges"));
const ReservationDeposits = lazyWithRetry(() => import("@/pages/admin/maintenance/ReservationDeposits"));
const AdditionalFees = lazyWithRetry(() => import("@/pages/admin/maintenance/AdditionalFees"));
const ZoneMappings = lazyWithRetry(() => import("@/pages/admin/maintenance/ZoneMappings"));
const MaintenanceHub = lazyWithRetry(() => import("@/pages/admin/maintenance/MaintenanceHub"));
const Paperwork = lazyWithRetry(() => import("@/pages/admin/Paperwork"));
const SiteAnalytics = lazyWithRetry(() => import("@/pages/admin/maintenance/SiteAnalytics"));
const NotFoundLog = lazyWithRetry(() => import("@/pages/admin/maintenance/NotFoundLog"));
const VideoTutorials = lazyWithRetry(() => import("@/pages/admin/maintenance/VideoTutorials"));
const FreightPartners = lazyWithRetry(() => import("@/pages/admin/maintenance/FreightPartners"));
const BulkyFees = lazyWithRetry(() => import("@/pages/admin/maintenance/BulkyFees"));
const SpecialtyPacking = lazyWithRetry(() => import("@/pages/admin/maintenance/SpecialtyPacking"));
const LocalDiscounts = lazyWithRetry(() => import("@/pages/admin/maintenance/LocalDiscounts"));
const LocalPricingTest = lazyWithRetry(() => import("@/pages/admin/maintenance/LocalPricingTest"));
const BrandingTemplates = lazyWithRetry(() => import("@/pages/admin/maintenance/BrandingTemplates"));
const DocumentTemplates = lazyWithRetry(() => import("@/pages/admin/maintenance/DocumentTemplates"));
const GenerateKeywordContent = lazyWithRetry(() => import("@/pages/admin/GenerateKeywordContent"));
const AnomalyEmailTemplates = lazyWithRetry(() => import("@/pages/admin/maintenance/AnomalyEmailTemplates"));
const SendTimeOptimization = lazyWithRetry(() => import("@/pages/admin/maintenance/SendTimeOptimization"));
const EmailPersonalization = lazyWithRetry(() => import("@/pages/admin/maintenance/EmailPersonalization"));
const PaymentAccounts = lazyWithRetry(() => import("@/pages/admin/maintenance/PaymentAccounts"));
const PricingGuides = lazyWithRetry(() => import("@/pages/admin/maintenance/PricingGuides"));
const TestModeSettings = lazyWithRetry(() => import("@/pages/admin/maintenance/TestModeSettings"));
const LaunchChecklist = lazyWithRetry(() => import("@/pages/admin/maintenance/LaunchChecklist"));
const AppMigration = lazyWithRetry(() => import("@/pages/admin/maintenance/AppMigration"));
const BrandSerpTracker = lazyWithRetry(() => import("@/pages/admin/maintenance/BrandSerpTracker"));
const AutomationMonitor = lazyWithRetry(() => import("@/pages/admin/maintenance/AutomationMonitor"));
const WebsiteLeads = lazyWithRetry(() => import("@/pages/admin/website/Leads"));
const WebsiteBookings = lazyWithRetry(() => import("@/pages/admin/website/Bookings"));
const DataQuality = lazyWithRetry(() => import("@/pages/admin/website/DataQuality"));
const APISources = lazyWithRetry(() => import("@/pages/admin/leads/APISources"));
const APIDocs = lazyWithRetry(() => import("@/pages/admin/leads/APIDocs"));
const LeadSourceTester = lazyWithRetry(() => import("@/pages/admin/maintenance/LeadSourceTester"));
const Archives = lazyWithRetry(() => import("@/pages/admin/settings/Archives"));
const LeadSourcesManager = lazyWithRetry(() => import("@/pages/admin/settings/LeadSourcesManager"));
const StorageManagement = lazyWithRetry(() => import("@/pages/admin/StorageManagement"));
const AdminBlog = lazyWithRetry(() => import("@/pages/admin/Blog"));
const BlogEditor = lazyWithRetry(() => import("@/pages/admin/BlogEditor"));
const BlogCategories = lazyWithRetry(() => import("@/pages/admin/BlogCategories"));
const GenerateBlogBatch = lazyWithRetry(() => import("@/pages/admin/GenerateBlogBatch"));
const GranotMigration = lazyWithRetry(() => import("@/pages/admin/GranotMigration"));
const LiveVisitors = lazyWithRetry(() => import("@/pages/admin/LiveVisitors"));
const Lost = lazyWithRetry(() => import("@/pages/admin/Lost"));

// Email Management pages
const EmailManagementDashboard = lazyWithRetry(() => import("@/pages/admin/email-management/Dashboard"));
const EmailCampaigns = lazyWithRetry(() => import("@/pages/admin/email-management/Campaigns"));
const EmailTemplates = lazyWithRetry(() => import("@/pages/admin/email-management/Templates"));
const EmailHistory = lazyWithRetry(() => import("@/pages/admin/email-management/History"));
const AutomationSettings = lazyWithRetry(() => import("@/pages/admin/email-management/AutomationSettings"));
const EmailSuppression = lazyWithRetry(() => import("@/pages/admin/email-management/Suppression"));
const EmailAnalytics = lazyWithRetry(() => import("@/pages/admin/email-management/Analytics"));
const EmailSegments = lazyWithRetry(() => import("@/pages/admin/email-management/Segments"));
const EmailSchedule = lazyWithRetry(() => import("@/pages/admin/email-management/Schedule"));
const CampaignBuilder = lazyWithRetry(() => import("@/pages/admin/email-management/CampaignBuilder"));
const ABTests = lazyWithRetry(() => import("@/pages/admin/email-management/ABTests"));
const DripSequences = lazyWithRetry(() => import("@/pages/admin/email-management/DripSequences"));
const EmailInbox = lazyWithRetry(() => import("@/pages/admin/email-management/Inbox"));
const CampaignDetails = lazyWithRetry(() => import("@/pages/admin/email-management/CampaignDetails"));
const Inbox = lazyWithRetry(() => import("@/pages/admin/email-management/Inbox"));
const SentEmailsList = lazyWithRetry(() => import("@/pages/admin/email-management/SentEmailsList"));
const ResponseRules = lazyWithRetry(() => import("@/pages/admin/email-management/ResponseRules"));
const ResponseTemplates = lazyWithRetry(() => import("@/pages/admin/email-management/ResponseTemplates"));

// SMS Management pages
const SMSDashboard = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSDashboard"));
const SMSSettings = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSSettings"));
const SMSInbox = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSInbox"));
const SMSResponseRules = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSResponseRules"));
const SMSResponseTemplates = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSResponseTemplates"));
const SMSAutomation = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSAutomation"));
const SMSCompose = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSCompose"));
const SMSAnalytics = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSAnalytics"));
const SMSBulkCampaigns = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSBulkCampaigns"));
const SMSOptOuts = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSOptOuts"));
const SMSQueue = lazyWithRetry(() => import("@/pages/admin/sms-management/SMSQueue"));
const AdminPartners = lazyWithRetry(() => import("@/pages/admin/Partners"));

/**
 * All admin route definitions as plain route elements.
 * This keeps the route list split from App.tsx without introducing
 * a wrapper component that React Router would reject inside <Routes>.
 */
export const adminRouteElements = (
  <>
    <Route index element={<Dashboard />} />
    <Route path="live-visitors" element={<LiveVisitors />} />
    <Route path="lost" element={<Lost />} />
    <Route path="reservations" element={<Reservations />} />
    <Route path="follow-up-calendar" element={<FollowUpCalendar />} />
    <Route path="sales-performance" element={<SalesPerformance />} />
    <Route path="website/leads" element={<WebsiteLeads />} />
    <Route path="website/bookings" element={<WebsiteBookings />} />
    <Route path="website/data-quality" element={<DataQuality />} />
    <Route path="leads/:type?" element={<Leads />} />
    <Route path="customers" element={<Customers />} />
    <Route path="jobs/local" element={<LocalJobs />} />
    <Route path="jobs/:type?" element={<Jobs />} />
    <Route path="bol/:jobId" element={<BillOfLading />} />
    <Route path="calendar/pickup" element={<Calendar />} />
    <Route path="calendar/trans-delivery" element={<TransDeliveryCalendar />} />
    <Route path="calendar/fleet-view" element={<FleetDispatchBoard />} />
    <Route path="tasks" element={<Tasks />} />
    <Route path="in-progress" element={<InProgress />} />
    <Route path="teams" element={<Teams />} />
    <Route path="teams/:teamId/performance" element={<TeamPerformance />} />
    <Route path="estimates" element={<Estimates />} />
    <Route path="accounting" element={<Accounting />} />
    <Route path="billing/invoices" element={<Navigate to="/admin/accounting" replace />} />
    <Route path="billing/cc-payment" element={<CCPayment />} />
    <Route path="billing/other-options" element={<OtherPaymentOptions />} />
    <Route path="fleet" element={<Fleet />} />
    <Route path="staff" element={<Staff />} />
    <Route path="reviews" element={<Reviews />} />
    <Route path="features" element={<FeaturesHub />} />
    <Route path="reports" element={<Reports />} />
    <Route path="reports/template-analytics" element={<TemplateAnalytics />} />
    <Route path="reports/inventory-reconciliation" element={<InventoryReconciliation />} />
    <Route path="reports/broker-deals" element={<BrokerDeals />} />
    <Route path="vibe-chat" element={<VibeChat />} />
    <Route path="vibe-capabilities" element={<VibeCapabilities />} />
    <Route path="paperwork" element={<Paperwork />} />
    <Route path="maintenance" element={<MaintenanceHub />} />
    <Route path="maintenance/analytics" element={<SiteAnalytics />} />
    <Route path="maintenance/not-found-log" element={<NotFoundLog />} />
    <Route path="maintenance/video-tutorials" element={<VideoTutorials />} />
    <Route path="maintenance/inventory" element={<InventoryCatalog />} />
    <Route path="maintenance/interstate-rates" element={<InterstateRates />} />
    <Route path="maintenance/local-rates" element={<LocalRateManager />} />
    <Route path="maintenance/volume-discounts" element={<VolumeDiscounts />} />
    <Route path="maintenance/surcharges" element={<Surcharges />} />
    <Route path="maintenance/reservation-deposits" element={<ReservationDeposits />} />
    <Route path="maintenance/additional-fees" element={<AdditionalFees />} />
    <Route path="maintenance/zone-mappings" element={<ZoneMappings />} />
    <Route path="maintenance/freight-partners" element={<FreightPartners />} />
    <Route path="maintenance/bulky-fees" element={<BulkyFees />} />
    <Route path="maintenance/specialty-packing" element={<SpecialtyPacking />} />
    <Route path="maintenance/local-discounts" element={<LocalDiscounts />} />
    <Route path="maintenance/local-pricing-test" element={<LocalPricingTest />} />
    <Route path="maintenance/api-sources" element={<APISources />} />
    <Route path="maintenance/api-docs" element={<APIDocs />} />
    <Route path="maintenance/lead-source-tester" element={<LeadSourceTester />} />
    <Route path="maintenance/branding-templates" element={<BrandingTemplates />} />
    <Route path="maintenance/document-templates" element={<DocumentTemplates />} />
    <Route path="maintenance/anomaly-email-templates" element={<AnomalyEmailTemplates />} />
    <Route path="maintenance/send-time-optimization" element={<SendTimeOptimization />} />
    <Route path="maintenance/email-personalization" element={<EmailPersonalization />} />
    <Route path="maintenance/payment-accounts" element={<PaymentAccounts />} />
    <Route path="maintenance/pricing-guides" element={<PricingGuides />} />
    <Route path="maintenance/test-mode" element={<TestModeSettings />} />
    <Route path="maintenance/launch-checklist" element={<LaunchChecklist />} />
    <Route path="maintenance/app-migration" element={<AppMigration />} />
    <Route path="maintenance/brand-serp-tracker" element={<BrandSerpTracker />} />
    <Route path="maintenance/automation-monitor" element={<AutomationMonitor />} />
    <Route path="settings" element={<Settings />} />
    <Route path="settings/archives" element={<Archives />} />
    <Route path="settings/lead-sources" element={<LeadSourcesManager />} />
    <Route path="storage-management" element={<StorageManagement />} />
    <Route path="settings/notifications" element={<Notifications />} />
    <Route path="maintenance/notification-preferences" element={<NotificationPreferences />} />
    <Route path="blog" element={<AdminBlog />} />
    <Route path="blog/new" element={<BlogEditor />} />
    <Route path="blog/:id/edit" element={<BlogEditor />} />
    <Route path="blog/categories" element={<BlogCategories />} />
    <Route path="blog/generate-batch" element={<GenerateBlogBatch />} />
    <Route path="email-management" element={<EmailManagementDashboard />} />
    <Route path="email-management/campaigns" element={<EmailCampaigns />} />
    <Route path="email-management/templates" element={<EmailTemplates />} />
    <Route path="email-management/history" element={<EmailHistory />} />
    <Route path="email-management/automation" element={<AutomationSettings />} />
    <Route path="email-management/suppression" element={<EmailSuppression />} />
    <Route path="email-management/analytics" element={<EmailAnalytics />} />
    <Route path="email-management/inbox" element={<Inbox />} />
    <Route path="email-management/sent" element={<SentEmailsList />} />
    <Route path="email-management/response-rules" element={<ResponseRules />} />
    <Route path="email-management/response-templates" element={<ResponseTemplates />} />
    <Route path="email-management/segments" element={<EmailSegments />} />
    <Route path="email-management/schedule" element={<EmailSchedule />} />
    <Route path="email-management/campaigns/new" element={<CampaignBuilder />} />
    <Route path="email-management/campaigns/:id" element={<CampaignDetails />} />
    <Route path="email-management/ab-tests" element={<ABTests />} />
    <Route path="email-management/drip-sequences" element={<DripSequences />} />
    <Route path="sms-management" element={<SMSDashboard />} />
    <Route path="sms-management/settings" element={<SMSSettings />} />
    <Route path="sms-management/inbox" element={<SMSInbox />} />
    <Route path="sms-management/response-rules" element={<SMSResponseRules />} />
    <Route path="sms-management/response-templates" element={<SMSResponseTemplates />} />
    <Route path="sms-management/automation" element={<SMSAutomation />} />
    <Route path="sms-management/compose" element={<SMSCompose />} />
    <Route path="sms-management/analytics" element={<SMSAnalytics />} />
    <Route path="sms-management/bulk-campaigns" element={<SMSBulkCampaigns />} />
    <Route path="sms-management/opt-outs" element={<SMSOptOuts />} />
    <Route path="sms-management/queue" element={<SMSQueue />} />
    <Route path="chat-management" element={<ChatManagement />} />
    <Route path="generate-keyword-content" element={<GenerateKeywordContent />} />
    <Route path="granot-migration" element={<GranotMigration />} />
    <Route path="partners" element={<AdminPartners />} />
  </>
);
